// src/pages/Login.jsx
import React from "react";
import { Link } from "react-router-dom";
import { SignIn } from "@clerk/clerk-react";
import { Check, Star } from "lucide-react";
import loginAvatar from "../assets/login-avatar.png";

// Social-proof avatars
import avatar1 from "../assets/avatar-1.jpg";
import avatar2 from "../assets/avatar-2.jpg";
import avatar3 from "../assets/avatar-3.jpg";
import avatar4 from "../assets/avatar-4.jpg";

const PANEL = "#111124";
const ACCENT = "#F82E88";

const AVATARS = [avatar1, avatar2, avatar3, avatar4];

const PAGE_GRADIENT =
  "radial-gradient(circle at top, #26266b 0, #050514 55%, #000 100%)";

export default function Login() {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{
        background: PAGE_GRADIENT,
      }}
    >
      {/* MAIN GRID */}
      <div className="w-full max-w-6xl mx-auto grid gap-10 lg:grid-cols-[0.9fr,1.1fr] items-center">
        {/* LEFT SIDE – Hero, Benefits, Avatar Quote (no logo) */}
        <div className="text-slate-50 lg:max-w-[520px] mx-auto lg:mx-0">
          {/* Main hero copy */}
          <div className="mb-6 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold leading-tight">
              Access your
              <span className="text-pink-400"> PixelAds</span> AI ad studio
            </h1>
            <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-xl mx-auto">
              Paste a product URL, let AI analyze the page, and get photoshoot
              images, avatar videos, and ready-to-launch ad creatives in minutes
              instead of days.
            </p>
          </div>

          {/* Benefits list */}
          <div className="space-y-2 text-sm text-slate-200 max-w-md mx-auto">
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-pink-400" />
              <span>Turn any product page into high-converting creatives.</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-pink-400" />
              <span>Photos, videos, captions, and watermarks in one place.</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-pink-400" />
              <span>Export-ready for Meta and Google in a few clicks.</span>
            </div>
          </div>

          {/* Avatar / Testimonial block */}
          <div
            className="mt-6 flex items-center gap-4 rounded-2xl border border-white/10 bg-white p-4 shadow-2xl max-w-md mx-auto"
            style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.75)" }}
          >
            <img
              src={loginAvatar}
              alt="PixelAds user"
              className="h-14 w-14 rounded-full object-cover border border-pink-500/70"
            />
            <div className="text-sm">
              <p className="text-slate-900 leading-snug">
                “PixelAds turned our basic product photos into ad creatives that
                actually converted. It feels like having a full design team on
                demand.”
              </p>
              <p className="mt-2 text-xs font-medium text-slate-500">
                Alex, Performance Marketer
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE – Clerk card + meta card */}
        <div className="flex justify-center">
          {/* Shared width for SignIn and meta card */}
          <div className="w-full max-w-[640px]">
            {/* MAIN SIGN-IN CARD */}
            <SignIn
              path="/login"
              routing="path"
              signUpUrl="/signup"
              afterSignInUrl="/analyze"
              appearance={{
                layout: {
                  socialButtonsPlacement: "top",
                  socialButtonsVariant: "blockButton",
                },
                elements: {
                  // Make Clerk's wrapper fill the column
                  rootBox: "w-full flex justify-center",

                  // Single card, styled like Signup
                  card:
                    "w-full bg-white text-slate-900 rounded-2xl border border-slate-100 " +
                    "shadow-[0_24px_60px_rgba(15,23,42,0.65)] px-6 sm:px-8 pt-7 pb-6 " +
                    "flex flex-col items-stretch",
                  headerTitle:
                    "text-xl sm:text-2xl font-semibold text-center text-[#4318FF]",
                  headerSubtitle:
                    "mt-2 text-xs sm:text-sm text-slate-500 text-center",

                  // Social login button
                  socialButtonsBlockButton:
                    "w-full border border-slate-200 bg-slate-50 text-slate-900 hover:bg-slate-100 text-sm " +
                    "rounded-xl py-2.5 mb-3 transition-colors duration-150 font-medium " +
                    "flex items-center justify-center gap-2",
                  socialButtonsProviderIcon__google: "h-4 w-4",

                  // Hide the “Last used” variants and badge
                  socialButtonsBlockButton__lastUsed: "hidden",
                  badge: "hidden",
                  badge__lastUsed: "hidden",

                  // Divider
                  dividerRow: "my-3 flex items-center",
                  dividerLine: "bg-slate-200",
                  dividerText:
                    "text-[11px] uppercase tracking-[0.18em] text-slate-400",

                  // Form fields
                  form: "space-y-3 w-full",
                  formField: "space-y-1 w-full",
                  formFieldLabel: "text-xs font-medium text-slate-700 mb-1",
                  formFieldInput:
                    "w-full bg-white text-slate-900 border border-[#dbdde1] rounded-xl text-sm " +
                    "placeholder:text-slate-400 focus:border-[#4318FF] focus:ring-0 " +
                    "px-3 py-2.5",
                  formFieldAction:
                    "text-xs text-pink-500 hover:text-pink-600 font-medium block text-right mt-1",

                  // Primary button (main CTA)
                  formButtonPrimary:
                    "mt-4 w-full rounded-xl py-3 text-sm font-medium " +
                    "bg-gradient-to-r from-[#605DFF] to-[#A855F7] text-white " +
                    "transition-all duration-200 " +
                    "hover:brightness-105 hover:-translate-y-[1px] hover:shadow-glow",

                  footer: "hidden",
                },
                variables: {
                  colorPrimary: ACCENT,
                  colorText: "#111827",
                  colorTextSecondary: "#4B5563",
                  colorBackground: PANEL,
                  borderRadius: "1rem",
                  fontSize: "14px",
                },
              }}
            />

            {/* META CARD – terms, avatars, CTA */}
            <div className="mt-3 mx-auto max-w-[400px] rounded-2xl bg-white bg-clip-border shadow-[0_18px_40px_rgba(15,23,42,0.55)] px-6 sm:px-8 py-4">
              {/* Terms */}
              <p className="text-[11px] sm:text-xs text-slate-500 text-center">
                By logging in you agree to our{" "}
                <Link
                  to="/terms"
                  className="font-medium text-pink-500 hover:text-pink-600"
                >
                  terms &amp; privacy
                </Link>
                .
              </p>

              {/* Avatars + “Used by …” */}
              <div className="mt-4 flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {AVATARS.map((src, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <img
                        key={i}
                        src={src}
                        alt="PixelAds user"
                        className="h-8 w-8 sm:h-9 sm:w-9 rounded-full border-2 border-white object-cover"
                      />
                    ))}
                  </div>
                  <div className="flex gap-1 text-yellow-400">
                    {[0, 1, 2, 3, 4].map((i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Star
                        key={i}
                        className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-[11px] sm:text-xs font-medium text-slate-600 text-center">
                  Used by founders &amp; marketers testing products daily.
                </p>
              </div>

              {/* Divider + signup link */}
              <div className="mt-4 border-t border-slate-200 pt-3 text-center text-[11px] sm:text-xs text-slate-600">
                <span className="font-medium text-slate-700">
                  Don&apos;t have an account yet?
                </span>
                <Link
                  to="/signup"
                  className="ml-1 font-medium text-pink-500 hover:text-pink-600"
                >
                  Create an account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FEATURE BAND – What users get */}
      <section className="mt-12 w-full max-w-5xl mx-auto text-center text-slate-100">
        <h3 className="text-xl sm:text-2xl font-semibold">
          We help you launch &amp; scale winning ads by offering
        </h3>
        <p className="mt-2 text-sm text-slate-400 max-w-2xl mx-auto">
          Turn product URLs into photoshoot images, avatar videos, and
          export-ready ad creatives in one PixelAds workspace.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { emoji: "✨", label: "AI Photoshoot Images" },
            { emoji: "🛒", label: "Product URL Import" },
            { emoji: "📣", label: "Ad Creative Images" },
            { emoji: "🎬", label: "Avatar Ad Videos" },
          ].map((item) => (
            <div
              key={item.label}
              className="group rounded-[24px] bg-gradient-to-br from-[#5E72E4] via-[#FC2783] to-[#F97316] p-[1px] shadow-[0_18px_45px_rgba(0,0,0,0.75)] transition-transform duration-150 hover:-translate-y-1 hover:shadow-glow"
            >
              <div className="flex h-full flex-col items-center justify-center rounded-[22px] bg-[#050516]/95 px-5 py-5 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-3xl">
                  <span aria-hidden="true">{item.emoji}</span>
                </div>
                <p className="text-sm sm:text-base font-semibold text-white">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
